import { useEffect, useMemo, useRef } from 'react';
import { t } from 'ttag';
import { useHistory } from '@core/router';
import { showYesNo } from './alerts';

/**
 * Warns the user before leaving the page (in-app navigation, back button, refresh or tab close)
 * while `when` is true. Call `bypass()` before a programmatic navigation that should not warn
 * (e.g. redirecting after a successful save).
 */
export function useUnsavedChangesPrompt(when: boolean) {
  const history = useHistory();
  const whenRef = useRef(when);
  whenRef.current = when;
  const bypassRef = useRef(false);

  useEffect(() => {
    const unblock = history.block((location, action) => {
      if (bypassRef.current) {
        bypassRef.current = false;
        return undefined;
      }
      if (!whenRef.current) return undefined;

      showYesNo(t`Unsaved Changes`, t`You have unsaved changes. Are you sure you want to leave this page?`, {
        btnYesLabel: t`Leave Page`,
        btnNoLabel: t`Stay`,
      }).subscribe(confirmed => {
        if (!confirmed) return;
        bypassRef.current = true;
        unblock();
        if (action === 'POP') history.goBack();
        else if (action === 'REPLACE') history.replace(location);
        else history.push(location);
      });

      return false;
    });

    return unblock;
  }, [history]);

  useEffect(() => {
    const listener = (e: BeforeUnloadEvent) => {
      if (!whenRef.current || bypassRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', listener);
    return () => window.removeEventListener('beforeunload', listener);
  }, []);

  return useMemo(
    () => ({
      bypass: () => {
        bypassRef.current = true;
      },
    }),
    [],
  );
}
